import React from 'react';

import ExpressStore from '@/stores/express';
import SkuStore from '@/stores/sku';

import ActionButton from '@/component/module_actions/action-button';
import SkuPanelChat from '@/component/module_quick_sku/chat';

import Payment from '@/common/payment';
import UrlParam from '@/common/url-param';

import ActionRule from './action-rule';

import './index.less';

// 底部行动点
export default function index(
  props: ActionTypes.Root & {
    inventory: InventoryTypes.Root;
    productId: string;
    sku?: SkuTypes.PropTypes;
    sourcingTradeAvailable: boolean;
    productEncryptId: string;
    abtest: DetailTypes.Abtest;
    hasLogistics?: boolean;
    productOwnMemberAlitalkEncryptId: string;
    umidToken: string;
    semiHosted: boolean;
    globalTradeAssuranceForBuyer: boolean;
    devData: DevTypes.Root & Record<string, any>;
    contactEncryptId?: string;
    i18n: DetailTypes.i18n;
    atmSecurityToken: string;
  }
) {
  const {
    sku = {},
    productIsHang,
    renderConfig,
    hasLogistics = false,
    devData,
    i18n,
    type = 'product',
    umidToken,
    productEncryptId,
    productOwnMemberAlitalkEncryptId,
    price,
    visitCountry,
    productId,
    semiHosted = false,
    contactEncryptId,
    globalTradeAssuranceForBuyer = true,
    moq,
    atmSecurityToken,
    abtest,
  } = props;

  const { showType } = renderConfig;
  // 配置地址：https://mse.alibaba-inc.com/pre/diamond/configlist/configedit?DataId=pdp.freedom.fantasy.pc.protocol.config&Group=pdp&tab=edit&collectionDetail=true&originRegion=pre
  let { buttonConfig, actionRule } = devData;
  const expressInfo = ExpressStore(state => state[type]);
  const skuInfo = SkuStore(state => state[type]);
  // 行动点实验PRD：https://aliyuque.antfin.com/hzpmxg/kg7h1z/dkprdeu2kg2ult6s
  // 实验地址： https://dataphant.alibaba-inc.com/#/dashboard/ab/cabin/10001/exp-detail/6977011/info
  const actionTest = abtest?.pc_tradable_action || 'control';
  // 分桶逻辑
  if (actionTest === 'test1' && devData.buttonConfig_test1) {
    buttonConfig = devData.buttonConfig_test1;
  }
  if (actionTest === 'test2' && devData.actionRule_test2) {
    actionRule = devData.actionRule_test2;
  }
  if (actionTest === 'test3' && devData.actionRule_test3) {
    actionRule = devData.actionRule_test3;
  }

  /*
   * 获取行动点展示规则
   * 1、无sku配置更新
   * 2、根据来源返回配置规则
   * showType指的是组件，在配置中配的，type只有商品和样品，showType很多种，方便组件配置
   */
  const getRule = () => {
    // 无sku
    if (showType === 'action' && Object.keys(sku).length === 0) {
      actionRule.productSku = actionRule.pageSku;
      return 'productSku';
    }
    // 主页面
    if (showType === 'action') {
      return 'pageSku';
    }
    // quick view
    // 新增通过不同channel读取不同行动点
    if (showType === 'quick') {
      const channel = UrlParam('channel');
      const quickRule = actionRule[`${channel}Sku`];
      if (quickRule) {
        return `${channel}Sku`;
      } else {
        return 'quickSku';
      }
    }
    // 弹窗
    if (showType === 'drawer') {
      if (type === 'product') {
        return 'productSku';
      }
      if (type === 'sample') {
        return 'sampleSku';
      }
    }
    return 'productSku';
  };

  const actionButton = actionRule[getRule()];
  // 传给aliTalk的参数
  const params = {
    productOwnMemberAlitalkEncryptId,
    productId,
    i18n,
    umidToken,
    productEncryptId,
    type,
    config: buttonConfig,
    renderConfig,
    price,
    moq,
    atmSecurityToken,
  };

  // 行动点实验： 是否展示po入口, 兜底询盘配置
  const getInquiryRule = () => {
    // 核心国家展示询盘
    if (semiHosted) {
      return actionButton.inquiryAction;
    }
    if (type === 'product') {
      if (globalTradeAssuranceForBuyer) {
        return actionButton.poAction;
      } else {
        return actionButton.inquiryAction;
      }
    }
    return actionButton.inquiryAction;
  };

  // DropShipping
  if (getRule() === 'pageSku' && window.location.search.indexOf('ecology_token') > 0) {
    return <ActionRule rule={getRule()} buttons={actionButton.dsAction} {...params} />;
  }

  // 商品挂起
  if (productIsHang === true) {
    if (renderConfig?.isSkuPanel && contactEncryptId) {
      return (
        <SkuPanelChat
          i18n={i18n}
          productId={productId}
          productOwnMemberAlitalkEncryptId={contactEncryptId}
        />
      );
    }
    return <ActionRule rule={getRule()} buttons={getInquiryRule()} {...params} />;
  }

  // 卖完
  // 半托管不支持
  if (skuInfo.isSoldOut === true && semiHosted === false) {
    return (
      <div className="button-layout">
        <ActionButton rule={getRule()} {...buttonConfig['message']} {...params} target="message" />
      </div>
    );
  }

  /**
   *  下单
   *  1、后端返回物流，前台再次确认
   *  2、样品直接下单
   *  2、主页面只弹窗，不包含表单
   *  3、下单包含表单
   */
  if (hasLogistics || expressInfo.isEmpty === false || type === 'sample') {
    // 主页面无需下单
    if (getRule() === 'pageSku') {
      const buttons = semiHosted ? actionButton.semiHostedAction : actionButton.orderAction;
      return <ActionRule rule={getRule()} buttons={buttons} {...params} />;
    }
    // 下单
    return (
      <Payment
        price={price}
        i18n={i18n}
        type={type}
        visitCountry={visitCountry}
        productId={productId}
        moq={moq}
        showType={showType}
      >
        <ActionRule rule={getRule()} buttons={actionButton.orderAction} {...params} />
      </Payment>
    );
  }

  // 询盘品
  return <ActionRule rule={getRule()} buttons={getInquiryRule()} {...params} />;
}
